// Balances.tsx — unified wallets page (Mobile Money + Cards) with internal tabs
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Smartphone, CreditCard } from 'lucide-react';
import { useLocalization } from '../contexts/LocalizationContext';
import MobileMoneyBalances from './MobileMoneyBalances';
import CardWallets from './CardWallets';

type Channel = 'mobile' | 'cards';

const Balances: React.FC = () => {
  const { t } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeChannel: Channel = searchParams.get('tab') === 'cards' ? 'cards' : 'mobile';

  const setChannel = (c: Channel) => {
    const next = new URLSearchParams(searchParams);
    if (c === 'mobile') next.delete('tab');
    else next.set('tab', c);
    setSearchParams(next, { replace: true });
  };

  const channels = useMemo(
    () => [
      { id: 'mobile' as const, label: 'Mobile Money', icon: Smartphone },
      { id: 'cards'  as const, label: 'Cards',         icon: CreditCard },
    ],
    []
  );

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-2">
        <div>
          <h1 className="text-[22px] font-bold text-slate-900 tracking-tight">
            {t('balances.title') || 'Balances'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('balances.subtitle') || 'Real-time overview of your payment wallets'}
          </p>
        </div>
      </div>

      {/* Channel tabs */}
      <div className="border-b border-slate-200">
        <div className="flex items-center gap-1">
          {channels.map((c) => {
            const Icon = c.icon;
            const active = activeChannel === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setChannel(c.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                  active
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-slate-500 hover:text-slate-900'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div>{activeChannel === 'mobile' ? <MobileMoneyBalances /> : <CardWallets />}</div>
    </div>
  );
};

export default Balances;
